// -----------------------------------------------------------------------------
// A6.8 Propose change (round 30 #9/#10, Kyle 30/Oct/26) — Project setup › Cover page › Client name / Project owner.
// Both print on every PDF cover, so a change waits for the super admin or an Administrator (request_propose, 0022):
// it appears on their Notifications page (Pending action) and under the sender's Pending request. A super admin /
// Administrator changes it straight away (button "Save"). A newer proposal for the same field replaces the waiting one.
// After sending, the field shows "Pending approval" (SetupPage CoverCard) until it is decided.
// -----------------------------------------------------------------------------
import { useState } from "react";
import { rpc } from "../../../core/api/backend";
import { ErrorBox, Modal, toast } from "../../../core/ui/kit";
import { refreshBadge } from "../../../core/notifications";

const LABEL = { client: "Client name", owner: "Project owner" } as const;
const TIP = ["Client name and Project owner print on every PDF cover, so a change needs approval.",
  "Approvers: the super admin and Administrators; it shows on their Notifications page (Pending action).",
  "You see it under Pending request; once decided it moves to Closed and your K badge counts it until you open it.",
  "Sending again for the same field replaces your waiting proposal."];

export function ProposeChangeModal({ code, field, current, direct, onClose, onDone }: {
  code: string; field: "client" | "owner"; current: string; direct: boolean; onClose: () => void; onDone: () => void;
}) {
  const [value, setValue] = useState(current);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await rpc("request_propose", { code, field, value: value.trim(), reason: reason.trim() });
      toast(r.applied ? `${LABEL[field]} saved` : `${LABEL[field]} sent for approval`);
      refreshBadge();
      onDone();
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  return (
    <Modal title={`${direct ? "Change" : "Propose change"} · ${LABEL[field]}`} info={TIP} onClose={onClose} className="propose-modal">
      <div className="field-box"><p className="k">Now</p><p className="v" data-testid="propose-now">{current || "–"}</p></div>
      <label className="field"><span>New {LABEL[field]}</span>
        <input autoFocus value={value} onChange={(e) => { setValue(e.target.value); setErr(null); }} aria-label={`New ${LABEL[field]}`} /></label>
      {!direct && <label className="field"><span>Reason <span className="faint">(optional)</span></span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" /></label>}
      {!direct && <p className="muted small">The super admin and Administrators will be notified. The cover keeps the current name until it is approved.</p>}
      {err && <ErrorBox message={err} />}
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy || !value.trim() || value.trim() === current} onClick={send}>{direct ? "Save" : "Send for approval"}</button>
      </div>
    </Modal>
  );
}
